import { eq, and, sql, desc, asc, isNull, isNotNull, gte, lte, inArray, getTableColumns } from 'drizzle-orm';
import { db, runInTransaction } from '@/db/client';
import {
    invoices,
    invoicePayments,
    transactions,
    creditCards,
    accounts,
    categories,
    subcategories,
    recurringTransactions,
    tags,
    transactionTags,
} from '@/db/schema';
import { generateId } from '@/lib/id';
import { resolveTransactionTime } from '@/lib/transactionTime';
import { roundMonetaryValue } from '@/lib/formatting';
import { recalculateAccountBalance } from './accountRepository';
import { recordHandledError } from '@/lib/crashReporting';
import type { TransactionTagInfo, TransactionWithRelations } from './transactionRepository';
import { AdjustmentType, TransactionType } from '@/lib/constants';
import { excludeInvoiceSettlementAdjustments } from './transactionScopes';
import { isSeedCatchAllName, isSeedAdjustmentsName } from '@/lib/seedCategoryNames';
import {
    format,
    parse,
    addMonths,
    isAfter,
} from 'date-fns';
import { calculateInvoiceDates, getInvoiceDisplayMonth, getInvoiceMonthForDate } from '@/lib/invoiceMonth';
import { hasInvoiceClosed } from '@/lib/invoiceClosing';
import { getCalendarMonthDateRange } from '@/lib/months';
import { effectiveDueDate } from '@/lib/businessDay';
import { buildProjectedRecurringTransactions } from '@/lib/projectedRecurringTransactions';
import { getTagInfoForRecurringIds } from './tagRepository';
import { compareIso } from '@/lib/compareIso';

// Reexportada daqui pra preservar a API pública do repositório (test de regressão e
// chamadores antigos importam de invoiceRepository). A lógica vive em lib/invoiceMonth.
export { calculateInvoiceDates };

export type Invoice = typeof invoices.$inferSelect;
export type InvoicePayment = typeof invoicePayments.$inferSelect;

export type InvoiceWithDetails = Invoice & {
    creditCardName?: string | null;
    creditCardColor?: string | null;
    creditCardBrand?: string | null;
    creditCardLimit?: number | null;
};

export { getInvoiceMonthForDate };

/**
 * Categoria/subcategoria dos lançamentos automáticos de fatura (crédito da fatura
 * anterior, saldo anterior, ajuste). Casa o balde do seed nos DOIS idiomas — ver
 * lib/seedCategoryNames: o nome no banco é o do `seed_locale`, e casar só "Outros"
 * deixava todo install em inglês sem categoria.
 */
async function getInvoiceAdjustmentCategoryTarget() {
    const expenseCategories = await db
        .select({ id: categories.id, name: categories.name })
        .from(categories)
        .where(
            and(
                eq(categories.type, 'expense'),
                isNull(categories.deletedAt),
            ),
        );

    const category = expenseCategories.find((row) => isSeedCatchAllName(row.name));

    if (!category) {
        return { categoryId: null, subcategoryId: null };
    }

    const categorySubcategories = await db
        .select({ id: subcategories.id, name: subcategories.name })
        .from(subcategories)
        .where(
            and(
                eq(subcategories.categoryId, category.id),
                isNull(subcategories.deletedAt),
            ),
        );

    // "Ajustes"/"Adjustments" é a mais específica; o balde é o fallback.
    const subcategory = categorySubcategories.find((row) => isSeedAdjustmentsName(row.name))
        ?? categorySubcategories.find((row) => isSeedCatchAllName(row.name));

    return {
        categoryId: category.id,
        subcategoryId: subcategory?.id ?? null,
    };
}

export async function getOrCreateInvoice(
    cardId: string,
    referenceMonth: string,
): Promise<Invoice> {
    const findInvoiceByCardAndMonth = () =>
        db
            .select()
            .from(invoices)
            .where(
                and(
                    eq(invoices.creditCardId, cardId),
                    eq(invoices.referenceMonth, referenceMonth),
                ),
            );

    // Try to find existing invoice
    const existing = await findInvoiceByCardAndMonth();

    if (existing[0]) return existing[0];

    // Get card info for closing/due days
    const card = await db.select().from(creditCards).where(eq(creditCards.id, cardId));
    if (!card[0]) throw new Error('Credit card not found');

    const { openingDate, closingDate, dueDate } = calculateInvoiceDates(
        referenceMonth,
        card[0].closingDay,
        card[0].dueDay,
        card[0].closingDayToNextInvoice === 1,
    );

    const id = generateId();
    const now = new Date().toISOString();

    await db
        .insert(invoices)
        .values({
            id,
            creditCardId: cardId,
            referenceMonth,
            openingDate,
            closingDate,
            dueDate,
            totalAmount: 0,
            paidAmount: 0,
            remainderAmount: 0,
            status: 'open',
            createdAt: now,
            updatedAt: now,
        })
        .onConflictDoNothing({
            target: [invoices.creditCardId, invoices.referenceMonth],
        });

    const result = await findInvoiceByCardAndMonth();
    if (!result[0]) {
        throw new Error(`Unable to create or load invoice for ${referenceMonth}`);
    }

    return result[0];
}

/**
 * Total e pago REAIS de uma fatura, pelo id.
 *
 * Existe para o pagamento nunca ser semeado a partir da LINHA que o usuário tocou. Na
 * lista do mês em competência a linha "Fatura X" é agregado calculado
 * (`buildAccrualInvoiceSummaryTransaction`), não a `INVOICE_EXPENSE`: ela não desconta
 * `paidAmount` e, com Parcelamento pelo valor total ligado (ADR 0043), nem vale a fatura.
 * Semear o PayInvoiceSheet dali pré-preenchia um pagamento com número fabricado. Ver ADR 0077.
 *
 * `closingDate` + `status` (o PERSISTIDO) vão junto para o sheet decidir, com
 * `hasInvoiceClosed`, se a fatura já fechou — o passo "Saldo restante" só existe aí.
 */
export async function getInvoiceById(
    invoiceId: string,
): Promise<
    { id: string; totalAmount: number; paidAmount: number; closingDate: string; status: string } | undefined
> {
    const rows = await db
        .select({
            id: invoices.id,
            totalAmount: invoices.totalAmount,
            paidAmount: invoices.paidAmount,
            closingDate: invoices.closingDate,
            status: invoices.status,
        })
        .from(invoices)
        .where(eq(invoices.id, invoiceId))
        .limit(1);

    return rows[0];
}

export async function getInvoiceByCardAndMonth(
    cardId: string,
    referenceMonth: string,
    options?: { onMaterializeTransactions?: () => void },
): Promise<InvoiceWithDetails | undefined> {
    const rows = await db
        .select({
            id: invoices.id,
            creditCardId: invoices.creditCardId,
            referenceMonth: invoices.referenceMonth,
            openingDate: invoices.openingDate,
            closingDate: invoices.closingDate,
            dueDate: invoices.dueDate,
            totalAmount: invoices.totalAmount,
            manualTotal: invoices.manualTotal,
            paidAmount: invoices.paidAmount,
            remainderAmount: invoices.remainderAmount,
            remainderAction: invoices.remainderAction,
            rolledFromInvoiceId: invoices.rolledFromInvoiceId,
            status: invoices.status,
            isManuallySet: invoices.isManuallySet,
            reconciledAt: invoices.reconciledAt,
            reconciledItemCount: invoices.reconciledItemCount,
            createdAt: invoices.createdAt,
            updatedAt: invoices.updatedAt,
            creditCardName: creditCards.name,
            creditCardColor: creditCards.color,
            creditCardBrand: creditCards.brand,
            creditCardLimit: creditCards.creditLimit,
            dueOnBusinessDay: creditCards.dueOnBusinessDay,
        })
        .from(invoices)
        .innerJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(
            and(
                eq(invoices.creditCardId, cardId),
                eq(invoices.referenceMonth, referenceMonth),
            ),
        );

    if (!rows[0]) return undefined;
    const result = {
        ...rows[0],
        status: computeInvoiceStatus(rows[0]),
    };

    if (result.totalAmount > 0) {
        const didMaterialize = await ensureInvoiceExpenseTransaction(result as Invoice);
        if (didMaterialize) {
            options?.onMaterializeTransactions?.();
        }
    }

    const projectedAmount = await getProjectedRecurringInvoiceAmount(result as Invoice);
    result.totalAmount += projectedAmount;

    return result;
}

export function computeInvoiceStatus(
    invoice: Pick<Invoice, 'totalAmount' | 'paidAmount' | 'closingDate' | 'dueDate' | 'status'>
        // Vencimento em dias úteis (ADR 0082): quem tem o flag do cartão na mão passa junto
        // e a virada pra "atrasada" respeita o vencimento ÚTIL. Sem o campo, vale a nominal —
        // inofensivo nos chamadores que só distinguem paid/closed (independem do dueDate).
        & { dueOnBusinessDay?: number | boolean | null },
): string {
    const { totalAmount, paidAmount, closingDate, dueDate } = invoice;
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayDate = parse(today, 'yyyy-MM-dd', new Date());
    const payableDueDate = effectiveDueDate(dueDate, invoice.dueOnBusinessDay);
    const isPastDue = isAfter(todayDate, parse(payableDueDate, 'yyyy-MM-dd', new Date()));

    // Tolerância de meio centavo: somas de float (ex.: 0.57 + 1.13 = 1.6999…)
    // não devem impedir a fatura de fechar como paga.
    if (totalAmount > 0 && paidAmount >= totalAmount - 0.005) return 'paid';
    if (paidAmount > 0) return isPastDue ? 'overdue' : 'partial';
    if (isPastDue && totalAmount > 0) return 'overdue';
    // Aberta × fechada: critério único em lib/invoiceClosing (o sheet de pagar e o resto de
    // fatura leem o mesmo). SÓ a data: um `closed` gravado com fechamento futuro (janela
    // reescrita no boot depois de mudar o dia de fechamento) voltou a ser aberta.
    if (hasInvoiceClosed({ closingDate }, todayDate)) return 'closed';
    return 'open';
}

export interface InvoiceMonthlyTotal {
    creditCardId: string;
    creditCardName: string;
    /** Mês em que a fatura VENCE (YYYY-MM) — é como ela se chama no app (ADR 0102). */
    displayMonth: string;
    totalAmount: number;
}

/**
 * Total de cada fatura num intervalo de meses, na coluna do mes em que ela VENCE.
 * Usa o mesmo `totalAmount` que o timeline do cartao exibe, e o mesmo mes que o timeline
 * nomeia (ADR 0102) — por isso o recorte e o bucket sao pelo vencimento nominal, nao pelo
 * mes de referencia. Cartoes excluidos (soft-delete) ficam de fora.
 */
export async function getInvoiceMonthlyTotals(
    startMonth: string,
    endMonth: string,
): Promise<InvoiceMonthlyTotal[]> {
    const rows = await db
        .select({
            creditCardId: invoices.creditCardId,
            creditCardName: creditCards.name,
            dueDate: invoices.dueDate,
            totalAmount: invoices.totalAmount,
        })
        .from(invoices)
        .innerJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(
            and(
                // Fatura é NOMEADA pelo mês-calendário do vencimento (ADR 0102); o Início do mês
                // do app (ADR 0190) não entra aqui de propósito.
                gte(invoices.dueDate, getCalendarMonthDateRange(startMonth).startDate),
                lte(invoices.dueDate, getCalendarMonthDateRange(endMonth).endDate),
                isNull(creditCards.deletedAt),
            ),
        );

    return rows.map(({ dueDate, ...row }) => ({
        ...row,
        displayMonth: getInvoiceDisplayMonth(dueDate),
    }));
}

/**
 * Resolve a conta de pagamento do cartão para ESCRITA: devolve `null` quando a
 * referência não aponta pra uma conta viva (apagada de fato ou soft-deletada).
 * Toda escrita que grava `transactions.account_id` a partir de
 * `credit_cards.payment_account_id` precisa passar por aqui — ver comentário no
 * chamador.
 */
async function resolvePaymentAccountId(paymentAccountId: string | null | undefined): Promise<string | null> {
    if (!paymentAccountId) return null;
    const rows = await db
        .select({ id: accounts.id })
        .from(accounts)
        .where(and(eq(accounts.id, paymentAccountId), isNull(accounts.deletedAt)));
    return rows[0]?.id ?? null;
}

/** Fila em memória, uma por fatura. Ver `ensureInvoiceExpenseTransaction`. */
const invoiceExpenseQueue = new Map<string, Promise<boolean>>();

/**
 * Ensure a pending expense transaction exists on the payment account for an invoice with amount > 0.
 * This creates an unconfirmed expense so it appears in projected balances.
 *
 * Returns `true` when it actually wrote to `transactions` (insert / update / soft-delete),
 * and `false` when nothing changed — so callers can invalidate dependent queries
 * (e.g. the transactions list) only when something really changed.
 *
 * SERIALIZADO por fatura. O corpo é read-then-write com dois `await` entre o SELECT do
 * `existing` e o INSERT (o select do cartão e o `resolvePaymentAccountId`), e chamadores
 * concorrentes tocam a MESMA fatura — a leitura da tela da fatura e a varredura de
 * `getInvoicesByDueMonth`/`getInvoicesByMonth` da Home, por exemplo. Os dois liam vazio e
 * os dois inseriam. A duplicata NÃO se cura sozinha (as branches de atualizar e de
 * soft-deletar só mexem em `existing[0]`): a segunda linha fica órfã pra sempre, contando
 * a fatura duas vezes no saldo previsto, no calendário de fluxo de caixa e no
 * `pendingExpense` do relatório. A fila resolve na raiz — o segundo chamador enxerga a
 * linha do primeiro e cai na branch de atualização.
 *
 * Fica AQUI dentro de propósito: os call-sites herdam sem precisar saber de nada. Um
 * único runtime JS basta — o outro contexto que escreve no banco é a task headless de
 * notificação, e ela só toca `pending_notifications`.
 */
function ensureInvoiceExpenseTransaction(invoice: Invoice): Promise<boolean> {
    const pending = invoiceExpenseQueue.get(invoice.id) ?? Promise.resolve(false);
    // `catch` na anterior: uma falha (ex.: SQLITE_BUSY) não pode envenenar a fila e
    // derrubar junto as chamadas seguintes desta mesma fatura.
    const next = pending.catch(() => false).then(() => runEnsureInvoiceExpenseTransaction(invoice));
    invoiceExpenseQueue.set(invoice.id, next);

    // Solta a entrada quando ESTA for a última da fila — segurar o Map indefinidamente
    // vazaria uma promise por fatura visitada na sessão. O `catch` aqui só evita
    // unhandled rejection nesta ramificação; o erro segue vivo no `next` devolvido.
    void next.catch(() => {}).then(() => {
        if (invoiceExpenseQueue.get(invoice.id) === next) invoiceExpenseQueue.delete(invoice.id);
    });

    return next;
}

async function runEnsureInvoiceExpenseTransaction(invoice: Invoice): Promise<boolean> {
    const status = computeInvoiceStatus(invoice);
    const existing = await db
        .select()
        .from(transactions)
        .where(
            and(
                eq(transactions.invoiceId, invoice.id),
                eq(transactions.adjustmentType, AdjustmentType.INVOICE_EXPENSE),
                eq(transactions.isConfirmed, 0),
                isNull(transactions.deletedAt),
            ),
        );

    const card = await db.select().from(creditCards).where(eq(creditCards.id, invoice.creditCardId));

    // Cartão excluído (ou inexistente) não materializa despesa prevista — era o que
    // ressuscitava a fatura zumbi (ADR 0068). A pendente que sobrou é soft-deletada aqui
    // mesmo, curando retroativamente qualquer caminho de leitura que ainda toque a fatura.
    if (!card[0] || card[0].deletedAt) {
        if (existing[0]) {
            await db
                .update(transactions)
                .set({ deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
                .where(eq(transactions.id, existing[0].id));
            return true;
        }
        return false;
    }

    const cardName = card[0]?.name ?? 'Cartão';
    // `payment_account_id` pode estar PENDURADO: apagar a conta é soft-delete e não
    // desvincula o cartão, e tanto o restore quanto o purge escrevem com FK OFF — a
    // conta some da tabela e o cartão continua apontando pra ela. Sem esta checagem o
    // insert abaixo estourava FOREIGN KEY em toda LEITURA de fatura (Sentry
    // REACT-NATIVE-7) e o usuário só via um toast de erro. Tratamos ref quebrada como
    // "cartão sem conta de pagamento" — o mesmo caminho de um cartão que nunca teve uma.
    const paymentAccountId = await resolvePaymentAccountId(card[0]?.paymentAccountId);
    const remainingAmount = Math.max(roundMonetaryValue(invoice.totalAmount - invoice.paidAmount), 0);
    // A despesa prevista É o evento de pagamento — carrega o vencimento ÚTIL (ADR 0082),
    // senão a linha do extrato fica vermelha no sábado enquanto o card diz "vence segunda".
    // O flag sai do card já buscado acima (fonte única), e o self-heal abaixo corrige a
    // data das pendentes existentes quando o toggle do cartão muda.
    const payableDueDate = effectiveDueDate(invoice.dueDate, card[0]?.dueOnBusinessDay);

    if (status === 'paid' || remainingAmount <= 0 || !paymentAccountId) {
        if (existing[0]) {
            await db
                .update(transactions)
                .set({ deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
                .where(eq(transactions.id, existing[0].id));
            return true;
        }
        return false;
    }

    if (existing[0]) {
        if (
            existing[0].amount !== remainingAmount ||
            existing[0].accountId !== paymentAccountId ||
            existing[0].date !== payableDueDate ||
            existing[0].description !== `Fatura ${cardName}`
        ) {
            await db
                .update(transactions)
                .set({
                    amount: remainingAmount,
                    accountId: paymentAccountId,
                    date: payableDueDate,
                    description: `Fatura ${cardName}`,
                    updatedAt: new Date().toISOString(),
                })
                .where(eq(transactions.id, existing[0].id));
            return true;
        }
        return false;
    }

    // Create pending expense on payment account
    const now = new Date().toISOString();
    await db.insert(transactions).values({
        id: generateId(),
        type: 'expense',
        amount: remainingAmount,
        description: `Fatura ${cardName}`,
        date: payableDueDate,
        accountId: paymentAccountId,
        creditCardId: invoice.creditCardId,
        invoiceId: invoice.id,
        adjustmentType: AdjustmentType.INVOICE_EXPENSE,
        isConfirmed: 0,
        createdAt: now,
        updatedAt: now,
    });

    return true;
}

export async function getInvoicesByMonth(
    referenceMonth: string,
): Promise<Invoice[]> {
    // leftJoin só pra carregar o flag do vencimento útil (ADR 0082) — o conjunto de faturas
    // não pode encolher: fatura órfã de cartão que não existe mais (restore parcial, era
    // pré-ADR 0068) segue entrando, com flag null → vencimento nominal.
    const rows = await db
        .select({ ...getTableColumns(invoices), dueOnBusinessDay: creditCards.dueOnBusinessDay })
        .from(invoices)
        .leftJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(eq(invoices.referenceMonth, referenceMonth));

    const result = await Promise.all(rows.map(async (row) => {
        const invoiceWithStatus = {
            ...row,
            status: computeInvoiceStatus(row),
        };

        // Ensure pending expense transactions exist for invoices with amounts
        if (invoiceWithStatus.totalAmount > 0) {
            await ensureInvoiceExpenseTransaction(invoiceWithStatus);
        }

        const projectedAmount = await getProjectedRecurringInvoiceAmount(invoiceWithStatus);
        invoiceWithStatus.totalAmount += projectedAmount;

        return invoiceWithStatus;
    }));

    return result;
}

export async function getInvoicesByDueMonth(
    dueMonth: string,
): Promise<Invoice[]> {
    const rows = await db
        .select({ ...getTableColumns(invoices), dueOnBusinessDay: creditCards.dueOnBusinessDay })
        .from(invoices)
        .leftJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(sql`substr(${invoices.dueDate}, 1, 7) = ${dueMonth}`);

    const result = rows.map((row) => ({
        ...row,
        status: computeInvoiceStatus(row),
    }));

    for (const row of result) {
        if (row.totalAmount > 0) {
            await ensureInvoiceExpenseTransaction(row);
        }
    }

    return result;
}

/** Uma fatura + suas compras (com tags) anotadas com o cartão dono — insumo do
 *  Relatório de faturas (recorte por FATURA, multi-cartão; docs/feedback/2026-08-09). */
export interface InvoiceReportCardData {
    invoiceId: string;
    cardId: string;
    cardName: string;
    cardColor: string;
    status: string;
    transactions: TransactionWithRelations[];
}

/**
 * Dados do Relatório de faturas: as faturas que VENCEM no mês pedido, dos cartões
 * escolhidos, cada uma com suas compras (tags incluídas — `getInvoiceTransactions` já
 * carrega). Leitura pura: cartão sem fatura persistida no mês simplesmente não aparece
 * (sem side effect de criar fatura/transação de despesa aqui).
 *
 * O recorte é por **vencimento nominal**, não por `referenceMonth`: o relatório é
 * multi-cartão e a fatura é nomeada pelo mês em que vence (ADR 0102). Recortar pela
 * identidade juntaria, sob o rótulo "setembro", a fatura de um cartão que vence em
 * setembro com a de outro (fecha 27, vence 10) que só vence em outubro — que é
 * justamente a conta que o usuário faz contra o app do banco.
 */
export async function getInvoicesReportData(
    displayMonth: string,
    cardIds: string[],
): Promise<InvoiceReportCardData[]> {
    if (cardIds.length === 0) return [];

    // Mês-calendário de propósito: o relatório de faturas é por mês de vencimento (ADR 0102).
    const { startDate, endDate } = getCalendarMonthDateRange(displayMonth);

    const rows = await db
        .select({
            invoice: invoices,
            cardName: creditCards.name,
            cardColor: creditCards.color,
            dueOnBusinessDay: creditCards.dueOnBusinessDay,
        })
        .from(invoices)
        .innerJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(and(
            gte(invoices.dueDate, startDate),
            lte(invoices.dueDate, endDate),
            inArray(invoices.creditCardId, cardIds),
        ));

    return Promise.all(rows.map(async (row) => ({
        invoiceId: row.invoice.id,
        cardId: row.invoice.creditCardId,
        cardName: row.cardName,
        cardColor: row.cardColor,
        status: computeInvoiceStatus({ ...row.invoice, dueOnBusinessDay: row.dueOnBusinessDay }),
        transactions: await getInvoiceTransactions(row.invoice.id, row.invoice),
    })));
}

export async function getInvoiceTransactions(
    invoiceId: string,
    // Callers that already loaded the invoice (e.g. the accrual aggregation loop) can pass
    // it here to skip a redundant per-invoice SELECT. computeInvoiceStatus recomputes from
    // these fields, so the result is identical to fetching the row fresh.
    preloadedInvoice?: Pick<
        Invoice,
        'creditCardId' | 'openingDate' | 'closingDate' | 'dueDate' | 'totalAmount' | 'paidAmount' | 'status'
    >,
): Promise<TransactionWithRelations[]> {
    let invoice = preloadedInvoice;
    if (!invoice) {
        const invoiceRows = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
        if (!invoiceRows[0]) return [];
        invoice = invoiceRows[0];
    }

    const rows = await db
        .select({
            id: transactions.id,
            type: transactions.type,
            amount: transactions.amount,
            description: transactions.description,
            date: transactions.date,
            time: transactions.time,
            notes: transactions.notes,
            accountId: transactions.accountId,
            creditCardId: transactions.creditCardId,
            invoiceId: transactions.invoiceId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
            transferId: transactions.transferId,
            categoryId: transactions.categoryId,
            subcategoryId: transactions.subcategoryId,
            isRecurring: transactions.isRecurring,
            recurringId: transactions.recurringId,
            recurringOccurrenceDate: transactions.recurringOccurrenceDate,
            isHiddenFromSubs: transactions.isHiddenFromSubs,
            isIgnored: transactions.isIgnored,
            installmentId: transactions.installmentId,
            installmentNumber: transactions.installmentNumber,
            installmentTotal: transactions.installmentTotal,
            financingId: transactions.financingId,
            financingEntryType: transactions.financingEntryType,
            financingInstallmentNumber: transactions.financingInstallmentNumber,
            personalLoanId: transactions.personalLoanId,
            personalLoanEntryType: transactions.personalLoanEntryType,
            adjustmentType: transactions.adjustmentType,
            isConfirmed: transactions.isConfirmed,
            createdAt: transactions.createdAt,
            updatedAt: transactions.updatedAt,
            deletedAt: transactions.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
        })
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(transactions.subcategoryId, subcategories.id))
        .where(
            and(
                eq(transactions.invoiceId, invoiceId),
                isNull(transactions.deletedAt),
                excludeInvoiceSettlementAdjustments(),
            ),
        )
        // Desempate intra-dia pela HORA da compra quando existe (notificação/OCR gravam;
        // NULL fica por último no DESC do SQLite) e só então pela ordem de cadastro.
        .orderBy(desc(transactions.date), desc(transactions.time), desc(transactions.createdAt));

    // Tags das compras da fatura (sempre carregadas: os chips são opt-in na UI, mas a
    // análise por tag precisa delas de qualquer forma). IN em lotes pra não estourar o
    // limite de variáveis do SQLite.
    const tagsByTransaction = new Map<string, TransactionTagInfo[]>();
    if (rows.length > 0) {
        const ids = rows.map((row) => row.id);
        const CHUNK = 400;
        for (let i = 0; i < ids.length; i += CHUNK) {
            const tagRows = await db
                .select({
                    transactionId: transactionTags.transactionId,
                    id: tags.id,
                    name: tags.name,
                    color: tags.color,
                })
                .from(transactionTags)
                .innerJoin(tags, eq(transactionTags.tagId, tags.id))
                .where(inArray(transactionTags.transactionId, ids.slice(i, i + CHUNK)))
                .orderBy(asc(tags.name));
            for (const tagRow of tagRows) {
                const list = tagsByTransaction.get(tagRow.transactionId) ?? [];
                list.push({ id: tagRow.id, name: tagRow.name, color: tagRow.color });
                tagsByTransaction.set(tagRow.transactionId, list);
            }
        }
    }

    const resolvedMaterialized = rows.map((row) => ({
        ...row,
        destinationAccountName: null,
        destinationAccountColor: null,
        financingName: null,
        financingStatus: null,
        financingTotalInstallments: null,
        tags: tagsByTransaction.get(row.id),
    }));

    // If invoice is already closed/paid, no need to project recurring transations.
    const status = computeInvoiceStatus(invoice);
    if (status === 'closed' || status === 'paid') {
        return resolvedMaterialized;
    }

    const projectedRows = await getProjectedRecurringForInvoice(invoice, new Set(
        resolvedMaterialized
            .filter((row) => row.recurringId)
            // dedup pela data AGENDADA (ver transactionRepository): imune a antecipação/adiamento
            .map((row) => `${row.recurringId}:${row.recurringOccurrenceDate ?? row.date}`)
    ));

    // A herança da tag da Regra agora acontece DENTRO do motor de projeção (ADR 0101) —
    // getProjectedRecurringForInvoice carrega as tags nas regras e o motor repassa. Antes
    // era um pós-processamento aqui, o único dos 17 call sites do motor que herdava.

    const combinedRows = [...resolvedMaterialized, ...projectedRows];

    // Mesma regra do SQL acima: date DESC, depois time DESC (sem hora vai pro fim do
    // dia), por último createdAt DESC.
    return combinedRows.sort((a, b) => {
        if (a.date !== b.date) {
            return compareIso(b.date, a.date);
        }
        const timeA = a.time ?? '';
        const timeB = b.time ?? '';
        if (timeA !== timeB) {
            return compareIso(timeB, timeA);
        }
        if (a.createdAt !== b.createdAt) {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
        }
        return 0;
    });
}

export async function recalculateInvoiceTotal(invoiceId: string): Promise<void> {
    // Exclude invoice_expense transactions from the total (they are on the payment account, not card purchases)
    const result = await db
        .select({
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.invoiceId, invoiceId),
                isNull(transactions.deletedAt),
                excludeInvoiceSettlementAdjustments(),
            ),
        );

    // Arredonda o SUM do SQLite: o double cru (ex.: 205.64000000000004) deixa um
    // restante infinitesimal contra o paidAmount (que é arredondado), e a fatura
    // quitada aparece como "R$ 0,00 vencida" na home e no timeline.
    const total = roundMonetaryValue(result[0]?.total ?? 0);
    const now = new Date().toISOString();

    await db
        .update(invoices)
        .set({ totalAmount: total, updatedAt: now })
        .where(eq(invoices.id, invoiceId));

    await updateInvoiceStatus(invoiceId);

    const invoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (invoice[0]) {
        await ensureInvoiceExpenseTransaction(invoice[0]);
    }
}

export async function updateInvoiceStatus(invoiceId: string): Promise<void> {
    // O status aqui é PERSISTIDO — o join carrega o flag do cartão pra virada de
    // "atrasada" respeitar o vencimento útil (ADR 0082).
    const invoice = await db
        .select({ ...getTableColumns(invoices), dueOnBusinessDay: creditCards.dueOnBusinessDay })
        .from(invoices)
        .leftJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(eq(invoices.id, invoiceId));
    if (!invoice[0]) return;

    const status = computeInvoiceStatus(invoice[0]);

    await db
        .update(invoices)
        .set({ status, updatedAt: new Date().toISOString() })
        .where(eq(invoices.id, invoiceId));
}

export async function closeInvoice(invoiceId: string): Promise<void> {
    await db
        .update(invoices)
        .set({ status: 'closed', updatedAt: new Date().toISOString() })
        .where(eq(invoices.id, invoiceId));
}

/**
 * Fatura quitada EFETIVA as compras que ficaram pendentes dentro dela.
 *
 * Compra no cartão não tem "efetivar" próprio: quem tira o dinheiro da conta é o pagamento
 * da fatura, e por isso o formato canônico da linha de cartão é `is_confirmed = 1` sem
 * conta (ver `updateInstallmentSeries` e `installmentRepository`). Só que várias portas a
 * criam PENDENTE de propósito — a recorrência materializada até o fim do mês (o previsto
 * que a notificação abate, ADR 0056) e a compra lançada com data futura. Faltava o outro
 * lado: nada devolvia essas linhas ao estado canônico, nem a data chegar nem a fatura ser
 * paga. Elas ficavam com o selo "Pendente" para sempre DENTRO de uma fatura paga e — o
 * dano de verdade — sumiam de todo relatório em "Concluídas", que é o padrão do
 * Personalizado e do Exportar.
 *
 * Só quando a fatura fecha em dinheiro (mesma tolerância de meio centavo do
 * `computeInvoiceStatus`): pagamento parcial não efetiva nada. Ficam de fora os artefatos
 * de fatura (a despesa "Fatura X" e a liquidação são do próprio fluxo de pagamento) e
 * qualquer linha com conta — aquela debita saldo e tem o efetivar dela.
 */
async function confirmSettledInvoiceEntries(invoice: Invoice): Promise<void> {
    const settled = invoice.totalAmount > 0 && invoice.paidAmount >= invoice.totalAmount - 0.005;
    if (!settled) return;

    await db
        .update(transactions)
        .set({ isConfirmed: 1, updatedAt: new Date().toISOString() })
        .where(
            and(
                eq(transactions.invoiceId, invoice.id),
                eq(transactions.isConfirmed, 0),
                isNull(transactions.deletedAt),
                isNotNull(transactions.creditCardId),
                isNull(transactions.accountId),
                isNull(transactions.adjustmentType),
            ),
        );
}

/**
 * Nota da liquidação de um pagamento de fatura — a IDENTIDADE do par
 * `invoice_payments` ↔ transação. Só `writeInvoicePayment` escreve; `deleteInvoicePayment`
 * acha a liquidação por ela (ver `findPaymentSettlements`).
 */
const INVOICE_PAYMENT_NOTE_PREFIX = 'invoice_payment:';

function invoicePaymentSettlementNote(paymentId: string): string {
    return `${INVOICE_PAYMENT_NOTE_PREFIX}${paymentId}`;
}

export interface InvoicePaymentOptions {
    /** Vai pro `invoice_payments.notes` — marca a origem do pagamento (ex.: cashback). */
    notes?: string;
    /** Descrição da liquidação no extrato da conta. Default: "Pagamento fatura {cartão}". */
    description?: string;
}

export async function payInvoice(
    invoiceId: string,
    accountId: string,
    amount: number,
    date: string,
    options?: InvoicePaymentOptions,
): Promise<void> {
    // Pagamento de fatura é DINHEIRO REAL em 3 escritas casadas (registro + transação de
    // liquidação + paidAmount): crash no meio deixava um pagamento órfão sem transação —
    // indeletável pela UI ("Related payment transaction not found") e inflando o paidAmount
    // no recompute. Atômico ou nada.
    await runInTransaction(() => writeInvoicePayment(invoiceId, accountId, amount, date, options));
}

/**
 * As escritas do `payInvoice`, SEM abrir transação: pra quem precisa casar o pagamento
 * com outra escrita no mesmo BEGIN (o cashback da fatura: receita + pagamento). Tem que
 * ser chamada de DENTRO de uma `runInTransaction` — e nunca envolver o `payInvoice`
 * numa (a fila serializa e o aninhamento trava).
 */
export async function writeInvoicePayment(
    invoiceId: string,
    accountId: string,
    amount: number,
    date: string,
    options?: InvoicePaymentOptions,
): Promise<void> {
    const invoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (!invoice[0]) throw new Error('Invoice not found');
    if (amount <= 0) throw new Error('Payment amount must be greater than zero');

    const paymentId = generateId();
    const now = new Date().toISOString();
    const card = await db.select().from(creditCards).where(eq(creditCards.id, invoice[0].creditCardId));
    const cardName = card[0]?.name ?? 'Cartão';

    // Create payment record
    await db.insert(invoicePayments).values({
        id: paymentId,
        invoiceId,
        accountId,
        amount,
        date,
        notes: options?.notes ?? null,
        createdAt: now,
    });

    // Register the cash movement as a standalone settlement entry.
    await db.insert(transactions).values({
        id: generateId(),
        type: 'expense',
        amount,
        description: options?.description ?? `Pagamento fatura ${cardName}`,
        date,
        // Dinheiro saindo da conta agora — carimba a hora quando o pagamento é de
        // HOJE. Os lançamentos sintéticos da fatura (saldo anterior, ajuste, juros)
        // ficam de fora: são datados no fechamento e nem exibem hora na lista.
        time: resolveTransactionTime(date),
        notes: invoicePaymentSettlementNote(paymentId),
        accountId,
        creditCardId: invoice[0].creditCardId,
        invoiceId,
        adjustmentType: AdjustmentType.INVOICE_PAYMENT,
        isConfirmed: 1,
        createdAt: now,
        updatedAt: now,
    });

    // Update invoice paid amount (arredonda pra evitar resíduo de float que
    // deixaria a fatura como "parcial" mesmo após quitar o restante)
    const newPaidAmount = roundMonetaryValue(invoice[0].paidAmount + amount);
    await db
        .update(invoices)
        .set({ paidAmount: newPaidAmount, updatedAt: now })
        .where(eq(invoices.id, invoiceId));

    // Recalculate account balance
    await recalculateAccountBalance(accountId);

    // Refresh projection and status with the new remaining balance.
    await updateInvoiceStatus(invoiceId);
    const updatedInvoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (updatedInvoice[0]) {
        await confirmSettledInvoiceEntries(updatedInvoice[0]);
        await ensureInvoiceExpenseTransaction(updatedInvoice[0]);
    }
}

type PaymentSettlementRef = { id: string; accountId: string | null };

/**
 * Liquidação(ões) VIVA(S) de um pagamento de fatura.
 *
 * A identidade do par é a NOTA (`invoice_payment:<id do pagamento>`), que só o
 * `writeInvoicePayment` escreve e nenhuma tela edita. Conta, valor, data e fatura NÃO entram
 * na busca: são justamente o que uma edição da liquidação muda — o arrasto "pendente → pago"
 * da aba Transações abria o sheet de efetivação, e mudar valor/conta/data (ou a data futura
 * que o sheet troca por hoje, re-resolvendo a fatura) fazia a busca antiga, que exigia os
 * quatro iguais, não achar mais nada (Sentry REACT-NATIVE-2M).
 *
 * O casamento por valor sobra só para liquidação SEM a nota (dado de antes de mar/2026) e
 * NUNCA pega linha cuja nota já é de outro pagamento: antes, com dois pagamentos de mesmo
 * valor, data e conta e o primeiro órfão, desfazer o primeiro apagava a liquidação do
 * segundo — e o segundo virava órfão.
 */
async function findPaymentSettlements(payment: InvoicePayment): Promise<PaymentSettlementRef[]> {
    const byNote = await db
        .select({ id: transactions.id, accountId: transactions.accountId })
        .from(transactions)
        .where(
            and(
                eq(transactions.notes, invoicePaymentSettlementNote(payment.id)),
                eq(transactions.adjustmentType, AdjustmentType.INVOICE_PAYMENT),
                isNull(transactions.deletedAt),
            ),
        );
    if (byNote.length > 0) return byNote;

    return db
        .select({ id: transactions.id, accountId: transactions.accountId })
        .from(transactions)
        .where(
            and(
                eq(transactions.invoiceId, payment.invoiceId),
                eq(transactions.accountId, payment.accountId),
                eq(transactions.amount, payment.amount),
                eq(transactions.date, payment.date),
                eq(transactions.adjustmentType, AdjustmentType.INVOICE_PAYMENT),
                isNull(transactions.deletedAt),
                // `substr` e não LIKE: no LIKE o `_` do prefixo é curinga.
                sql`(${transactions.notes} IS NULL OR substr(${transactions.notes}, 1, ${INVOICE_PAYMENT_NOTE_PREFIX.length}) <> ${INVOICE_PAYMENT_NOTE_PREFIX})`,
            ),
        )
        // A que nasceu no mesmo instante do pagamento primeiro (era o 2º passe da busca antiga).
        .orderBy(sql`${transactions.createdAt} = ${payment.createdAt} DESC`, desc(transactions.createdAt), desc(transactions.id))
        .limit(1);
}

/** A liquidação existiu e foi pra lixeira (seleção do extrato → Excluir)? Só pro relato. */
async function hasDeletedSettlement(paymentId: string): Promise<boolean> {
    const rows = await db
        .select({ id: transactions.id })
        .from(transactions)
        .where(
            and(
                eq(transactions.notes, invoicePaymentSettlementNote(paymentId)),
                eq(transactions.adjustmentType, AdjustmentType.INVOICE_PAYMENT),
                isNotNull(transactions.deletedAt),
            ),
        )
        .limit(1);
    return rows.length > 0;
}

/**
 * O lado da FATURA de desfazer um pagamento (o registro já saiu de `invoice_payments`):
 * reverte a rolagem e volta o `paidAmount` à soma dos pagamentos que sobraram.
 */
async function reopenInvoiceAfterPaymentRemoval(sourceInvoice: Invoice, now: string): Promise<void> {
    // Desfazer um pagamento que disparou tratamento de saldo (rollToNext/adjust/
    // creditToNext) tem que reverter TAMBÉM o rollover — senão o "Saldo anterior"
    // (ou o "Crédito da fatura anterior") fica órfão na próxima fatura, contado em
    // dobro, e o paidAmount continua inflado. Cada pagamento tem a própria lixeira
    // (0cae6b0, 1.0.38), então isto vale também para um pagamento que não é o último: o
    // resto foi calculado com ele contado e deixa de valer quando ele sai.
    // Exige rolledAmount > 0: a transação a reverter é casada por valor, então um
    // remainderAmount 0/null (só possível em base corrompida) casaria, por engano,
    // uma transação de valor 0 não relacionada.
    const rolledAmount = sourceInvoice.remainderAmount ?? 0;
    if (sourceInvoice.remainderAction && rolledAmount > 0) {
        if (
            sourceInvoice.remainderAction === 'rollToNext' ||
            sourceInvoice.remainderAction === 'creditToNext'
        ) {
            // remainderAmount é sempre POSITIVO; o crédito por pagamento a maior
            // gravou a transação com sinal invertido, então o casamento por valor
            // tem que seguir o sinal — sem isso o crédito nunca seria encontrado.
            const rolledTransactionAmount = sourceInvoice.remainderAction === 'creditToNext'
                ? -rolledAmount
                : rolledAmount;
            const refDate = parse(`${sourceInvoice.referenceMonth}-01`, 'yyyy-MM-dd', new Date());
            const nextMonth = format(addMonths(refDate, 1), 'yyyy-MM');
            const nextInvoiceRows = await db
                .select()
                .from(invoices)
                .where(
                    and(
                        eq(invoices.creditCardId, sourceInvoice.creditCardId),
                        eq(invoices.referenceMonth, nextMonth),
                    ),
                );
            const nextInvoice = nextInvoiceRows[0];
            // Só mexe na próxima fatura se ela de fato herdou DESTA (evita apagar um
            // "Saldo anterior" vindo de outro mês).
            if (nextInvoice && nextInvoice.rolledFromInvoiceId === sourceInvoice.id) {
                await db
                    .update(transactions)
                    .set({ deletedAt: now, updatedAt: now })
                    .where(
                        and(
                            eq(transactions.invoiceId, nextInvoice.id),
                            eq(transactions.adjustmentType, AdjustmentType.INVOICE_REMAINDER),
                            eq(transactions.amount, rolledTransactionAmount),
                            isNull(transactions.deletedAt),
                        ),
                    );
                await db
                    .update(invoices)
                    .set({ rolledFromInvoiceId: null, updatedAt: now })
                    .where(eq(invoices.id, nextInvoice.id));
                await recalculateInvoiceTotal(nextInvoice.id);
                await updateInvoiceStatus(nextInvoice.id);
            }
        } else if (sourceInvoice.remainderAction === 'adjust') {
            // O estorno é um ajuste NEGATIVO na própria fatura (amount = -rolledAmount).
            await db
                .update(transactions)
                .set({ deletedAt: now, updatedAt: now })
                .where(
                    and(
                        eq(transactions.invoiceId, sourceInvoice.id),
                        eq(transactions.adjustmentType, AdjustmentType.INVOICE_REMAINDER),
                        eq(transactions.amount, -rolledAmount),
                        isNull(transactions.deletedAt),
                    ),
                );
            await recalculateInvoiceTotal(sourceInvoice.id);
        }

        await db
            .update(invoices)
            .set({ remainderAmount: 0, remainderAction: null, updatedAt: now })
            .where(eq(invoices.id, sourceInvoice.id));
    }

    // paidAmount tem que refletir os pagamentos REAIS restantes. Subtrair o valor do
    // pagamento não serve: o rollToNext/adjust força paidAmount = total, então a
    // subtração deixaria um resíduo (= total − valorDesfeito). Recalcular pela soma
    // dos invoice_payments restantes corrige isso em qualquer cenário — e não tem como
    // descontar duas vezes.
    const remainingPayments = await db
        .select()
        .from(invoicePayments)
        .where(eq(invoicePayments.invoiceId, sourceInvoice.id));
    const recomputedPaid = roundMonetaryValue(
        remainingPayments.reduce((sum, item) => sum + item.amount, 0),
    );
    await db
        .update(invoices)
        .set({ paidAmount: Math.max(recomputedPaid, 0), updatedAt: now })
        .where(eq(invoices.id, sourceInvoice.id));
}

type InvoicePaymentUndoAnomaly =
    | { kind: 'orphan'; settlement: 'deleted' | 'missing' }
    | { kind: 'invoice_missing' };

/**
 * Desfaz um pagamento de fatura: tira o registro de `invoice_payments`, apaga (soft) a
 * liquidação, reverte a rolagem e recalcula fatura e conta(s).
 *
 * Pagamento ÓRFÃO (nenhuma liquidação viva — a pessoa excluiu a linha "Pagamento fatura X"
 * pela seleção do extrato, ou ela sumiu num pagamento de antes da 1.0.30, não atômico) sai
 * do mesmo jeito, SEM erro. A saída de dinheiro já não está no saldo da conta (linha excluída
 * não conta), então tirar o registro deixa fatura e conta dizendo a mesma coisa. Lançar erro
 * — o que havia antes — deixava o pagamento impossível de desfazer, a fatura "paga" e o
 * dinheiro de volta na conta: patrimônio inflado sem saída pela UI (Sentry REACT-NATIVE-2M,
 * 14 toques em 5 s). A liquidação que está na lixeira fica lá.
 *
 * Pagamento de cashback (ADR 0148, `invoice_payments.notes = cashback:<receita>`): sai só o
 * pagamento com a liquidação; a receita FICA. "Apagar um lado não apaga o outro" é decisão
 * daquela ADR (cascata só quando o dado mostrar que acontece).
 *
 * Tudo — inclusive as LEITURAS — numa transação só: a fila do `runInTransaction` serializa,
 * então o segundo toque de um duplo toque lê depois do COMMIT do primeiro e sai no `return`.
 */
export async function deleteInvoicePayment(paymentId: string): Promise<void> {
    const anomaly = await runInTransaction(async (): Promise<InvoicePaymentUndoAnomaly | null> => {
        const [payment] = await db
            .select()
            .from(invoicePayments)
            .where(eq(invoicePayments.id, paymentId));
        if (!payment) return null;

        const settlements = await findPaymentSettlements(payment);
        const orphanSettlement = settlements.length === 0
            ? ((await hasDeletedSettlement(paymentId)) ? 'deleted' : 'missing')
            : null;
        const now = new Date().toISOString();

        await db.delete(invoicePayments).where(eq(invoicePayments.id, paymentId));

        if (settlements.length > 0) {
            await db
                .update(transactions)
                .set({ deletedAt: now, updatedAt: now })
                .where(inArray(transactions.id, settlements.map((settlement) => settlement.id)));
        }

        // Referência pendurada (restore/purge antigos escreviam com FK OFF): sem fatura não há
        // o que reabrir, mas o registro e a liquidação saem igual — ficar seria um pagamento
        // que tela nenhuma mostra e que segue tirando dinheiro da conta.
        const [sourceInvoice] = await db
            .select()
            .from(invoices)
            .where(eq(invoices.id, payment.invoiceId));
        if (sourceInvoice) {
            await reopenInvoiceAfterPaymentRemoval(sourceInvoice, now);
        }

        // A conta ATUAL da liquidação também: trocada pelo sheet de efetivação, ela é outra
        // que não a do registro — e o saldo cacheado dela ficaria contando a saída apagada.
        // A fatura atual dela (se mudou) não precisa: liquidação não entra no total.
        const affectedAccounts = new Set<string>([payment.accountId]);
        for (const settlement of settlements) {
            if (settlement.accountId) affectedAccounts.add(settlement.accountId);
        }
        for (const accountId of affectedAccounts) {
            await recalculateAccountBalance(accountId);
        }

        if (sourceInvoice) {
            await updateInvoiceStatus(sourceInvoice.id);
            const updatedInvoice = await db.select().from(invoices).where(eq(invoices.id, sourceInvoice.id));
            if (updatedInvoice[0]) {
                await ensureInvoiceExpenseTransaction(updatedInvoice[0]);
            }
        }

        if (!sourceInvoice) return { kind: 'invoice_missing' };
        if (orphanSettlement) return { kind: 'orphan', settlement: orphanSettlement };
        return null;
    });

    // Depois do COMMIT: o relato mede quantas bases carregam pagamento órfão (a origem que
    // ninguém provou), sem valor nem texto — só o tipo e se a liquidação estava na lixeira.
    if (anomaly?.kind === 'orphan') {
        recordHandledError(new Error('Pagamento de fatura desfeito sem a liquidação'), {
            kontto_op: 'invoice_payment_orphan_removed',
            settlement: anomaly.settlement,
        });
    } else if (anomaly?.kind === 'invoice_missing') {
        recordHandledError(new Error('Pagamento de fatura desfeito com a fatura ausente'), {
            kontto_op: 'invoice_payment_invoice_missing',
        });
    }
}

/**
 * Handle remainder after paying an invoice.
 * - 'rollToNext': creates a transaction on the next month's invoice for the remaining amount
 * - 'adjust': creates a negative adjustment on the current invoice to zero it out
 *
 * Só para fatura FECHADA (ADR 0085, atualização de 17/09/2026) — ver a trava abaixo.
 */
export async function handleInvoiceRemainder(
    invoiceId: string,
    remainderAmount: number,
    action: 'rollToNext' | 'adjust',
): Promise<void> {
    const invoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (!invoice[0]) return;

    // Resto de fatura só existe em fatura que já FECHOU. A aberta ainda recebe compras: o que
    // falta nela não é resto, é o que ainda vai ser cobrado. Rodar aqui força
    // paidAmount = totalAmount e efetiva as pendentes — pagou 60 de 100 pra liberar limite,
    // o app registrava 100 e marcava paga (Leonardo, 1.0.62). O PayInvoiceSheet não oferece
    // o passo nessa fatura; isto é a defesa do repositório. Erro e não no-op, ANTES de
    // qualquer escrita: um no-op deixaria o hook mostrar "Saldo levado para a próxima
    // fatura" sobre uma operação que não aconteceu — e chegar aqui é bug de tela (Sentry).
    if (!hasInvoiceClosed(invoice[0])) {
        throw new Error('Invoice remainder requires a closed invoice');
    }

    const card = await db.select().from(creditCards).where(eq(creditCards.id, invoice[0].creditCardId));
    const cardName = card[0]?.name ?? 'Cartão';
    const now = new Date().toISOString();
    const { categoryId, subcategoryId } = await getInvoiceAdjustmentCategoryTarget();

    // Update remainder info on the source invoice
    await db
        .update(invoices)
        .set({ remainderAmount, remainderAction: action, updatedAt: now })
        .where(eq(invoices.id, invoiceId));

    if (action === 'rollToNext') {
        // Calculate next month
        const refDate = parse(invoice[0].referenceMonth + '-01', 'yyyy-MM-dd', new Date());
        const nextMonth = format(addMonths(refDate, 1), 'yyyy-MM');

        // Get or create next month's invoice
        const nextInvoice = await getOrCreateInvoice(invoice[0].creditCardId, nextMonth);

        // Set rolledFromInvoiceId on the destination invoice
        await db
            .update(invoices)
            .set({ rolledFromInvoiceId: invoiceId, updatedAt: now })
            .where(eq(invoices.id, nextInvoice.id));

        // Add a transaction to the next invoice for the remainder
        await db.insert(transactions).values({
            id: generateId(),
            type: 'expense',
            amount: remainderAmount,
            description: `Saldo anterior - ${cardName}`,
            date: nextInvoice.openingDate,
            creditCardId: invoice[0].creditCardId,
            invoiceId: nextInvoice.id,
            categoryId,
            subcategoryId,
            adjustmentType: AdjustmentType.INVOICE_REMAINDER,
            isConfirmed: 1,
            createdAt: now,
            updatedAt: now,
        });

        await recalculateInvoiceTotal(nextInvoice.id);
    } else {
        // Create a negative adjustment on this invoice to zero it out
        await db.insert(transactions).values({
            id: generateId(),
            type: 'expense',
            amount: -remainderAmount,
            description: `Ajuste de fatura - ${cardName}`,
            date: invoice[0].closingDate,
            creditCardId: invoice[0].creditCardId,
            invoiceId: invoiceId,
            categoryId,
            subcategoryId,
            adjustmentType: AdjustmentType.INVOICE_REMAINDER,
            isConfirmed: 1,
            createdAt: now,
            updatedAt: now,
        });

        await recalculateInvoiceTotal(invoiceId);
    }

    // Mark invoice as paid since the remainder is handled
    const updatedInvoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (updatedInvoice[0]) {
        await db
            .update(invoices)
            .set({
                paidAmount: updatedInvoice[0].totalAmount,
                updatedAt: now,
            })
            .where(eq(invoices.id, invoiceId));
        await updateInvoiceStatus(invoiceId);
        const refreshed = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
        if (refreshed[0]) {
            // Rolar/ajustar o restante também quita a fatura — mesmo fecho do payInvoice,
            // senão a compra pendente ficava presa em "Pendente" por esta porta.
            await confirmSettledInvoiceEntries(refreshed[0]);
            await ensureInvoiceExpenseTransaction(refreshed[0]);
        }
    }
}

export type OverpaymentAction = 'creditAccount' | 'creditToNext';

/**
 * Handle overpayment (pagou MAIS que a fatura):
 * - 'creditAccount': devolve o excedente como receita na conta de pagamento.
 * - 'creditToNext': leva o excedente como CRÉDITO para a fatura seguinte — o
 *   dinheiro saiu de verdade do banco e o crédito fica com o cartão, então a
 *   próxima fatura já nasce abatida. É o espelho exato do 'rollToNext' do saldo
 *   restante, com o sinal invertido; reusa INVOICE_REMAINDER de propósito (o
 *   tipo já sai de toda leitura analítica e a UI já o colore pelo sinal).
 */
export async function handleInvoiceOverpayment(
    invoiceId: string,
    overpaymentAmount: number,
    accountId: string,
    action: OverpaymentAction = 'creditAccount',
): Promise<void> {
    const invoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (!invoice[0]) return;

    const card = await db.select().from(creditCards).where(eq(creditCards.id, invoice[0].creditCardId));
    const cardName = card[0]?.name ?? 'Cartão';
    const now = new Date().toISOString();

    if (action === 'creditToNext') {
        if (overpaymentAmount <= 0) return;
        const { categoryId, subcategoryId } = await getInvoiceAdjustmentCategoryTarget();

        // 4 escritas casadas (marca na origem + fatura destino + lançamento + recompute):
        // crash no meio deixaria um crédito órfão na próxima fatura. Mesma razão do
        // payInvoice — atômico ou nada. NÃO pode ser chamado de dentro de outra
        // runInTransaction (a fila serializa e um aninhamento trava).
        await runInTransaction(async () => {
            // A marca na fatura de origem é o que permite ao deleteInvoicePayment
            // desfazer o crédito junto com o pagamento que o gerou.
            await db
                .update(invoices)
                .set({ remainderAmount: overpaymentAmount, remainderAction: action, updatedAt: now })
                .where(eq(invoices.id, invoiceId));

            const refDate = parse(invoice[0].referenceMonth + '-01', 'yyyy-MM-dd', new Date());
            const nextMonth = format(addMonths(refDate, 1), 'yyyy-MM');
            const nextInvoice = await getOrCreateInvoice(invoice[0].creditCardId, nextMonth);

            await db
                .update(invoices)
                .set({ rolledFromInvoiceId: invoiceId, updatedAt: now })
                .where(eq(invoices.id, nextInvoice.id));

            // Valor NEGATIVO: abate a fatura seguinte. Linha explícita no extrato da
            // fatura (getInvoiceTransactions não filtra INVOICE_REMAINDER) para o
            // crédito ficar rastreável, e não embutido num total sem origem.
            await db.insert(transactions).values({
                id: generateId(),
                type: 'expense',
                amount: -overpaymentAmount,
                description: `Crédito da fatura anterior - ${cardName}`,
                date: nextInvoice.openingDate,
                creditCardId: invoice[0].creditCardId,
                invoiceId: nextInvoice.id,
                categoryId,
                subcategoryId,
                adjustmentType: AdjustmentType.INVOICE_REMAINDER,
                isConfirmed: 1,
                createdAt: now,
                updatedAt: now,
            });

            await recalculateInvoiceTotal(nextInvoice.id);
        });
        return;
    }

    // Create income transaction on the payment account for the overpayment
    await db.insert(transactions).values({
        id: generateId(),
        type: 'income',
        amount: overpaymentAmount,
        description: `Crédito fatura ${cardName}`,
        date: format(new Date(), 'yyyy-MM-dd'),
        accountId,
        adjustmentType: AdjustmentType.INVOICE_CREDIT,
        isConfirmed: 1,
        createdAt: now,
        updatedAt: now,
    });

    await recalculateAccountBalance(accountId);
}

export type InvoicePaymentWithAccount = InvoicePayment & {
    accountName?: string | null;
    accountColor?: string | null;
};

export async function getInvoicePayments(invoiceId: string): Promise<InvoicePaymentWithAccount[]> {
    return db
        .select({
            id: invoicePayments.id,
            invoiceId: invoicePayments.invoiceId,
            accountId: invoicePayments.accountId,
            amount: invoicePayments.amount,
            date: invoicePayments.date,
            notes: invoicePayments.notes,
            createdAt: invoicePayments.createdAt,
            accountName: accounts.name,
            accountColor: accounts.color,
        })
        .from(invoicePayments)
        .leftJoin(accounts, eq(invoicePayments.accountId, accounts.id))
        .where(eq(invoicePayments.invoiceId, invoiceId))
        // createdAt desempata pagamentos do mesmo dia — `date` não tem hora, e
        // "desfazer último pagamento" confia que a primeira linha é o último.
        .orderBy(desc(invoicePayments.date), desc(invoicePayments.createdAt));
}

export async function getInvoiceHistory(cardId: string): Promise<Invoice[]> {
    const rows = await db
        .select({ ...getTableColumns(invoices), dueOnBusinessDay: creditCards.dueOnBusinessDay })
        .from(invoices)
        .leftJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(eq(invoices.creditCardId, cardId))
        .orderBy(desc(invoices.referenceMonth));

    const result = await Promise.all(rows.map(async (row) => {
        const invoiceWithStatus = {
            ...row,
            status: computeInvoiceStatus(row),
        };

        // A paid invoice is settled, so the two per-invoice operations below are no-ops:
        // getProjectedRecurringInvoiceAmount returns 0 for paid invoices, and
        // ensureInvoiceExpenseTransaction would at most clean up a pending expense that
        // the payment flow already removes. Skipping both for paid invoices avoids an
        // N+1 that grows with the card's age, without changing the totals/status the
        // history shows. NOTE: only `paid` — a `closed` (unpaid) invoice must still keep
        // its pending expense, so it goes through the full path.
        if (invoiceWithStatus.status === 'paid') {
            return invoiceWithStatus;
        }

        if (invoiceWithStatus.totalAmount > 0) {
            await ensureInvoiceExpenseTransaction(invoiceWithStatus);
        }

        const projectedAmount = await getProjectedRecurringInvoiceAmount(invoiceWithStatus);
        invoiceWithStatus.totalAmount += projectedAmount;

        return invoiceWithStatus;
    }));

    return result;
}

export async function setManualTotal(invoiceId: string, amount: number): Promise<void> {
    const invoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (!invoice[0]) return;

    const now = new Date().toISOString();

    // Save manual total flag
    await db
        .update(invoices)
        .set({ manualTotal: amount, isManuallySet: 1, updatedAt: now })
        .where(eq(invoices.id, invoiceId));

    // Calculate current total WITHOUT any existing manual adjustment
    const result = await db
        .select({
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.invoiceId, invoiceId),
                isNull(transactions.deletedAt),
                sql`(${transactions.adjustmentType} IS NULL OR (${transactions.adjustmentType} != ${AdjustmentType.INVOICE_EXPENSE} AND ${transactions.adjustmentType} != ${AdjustmentType.INVOICE_MANUAL_ADJUSTMENT} AND ${transactions.adjustmentType} != ${AdjustmentType.INVOICE_PAYMENT}))`,
            ),
        );

    const calculatedTotal = result[0]?.total ?? 0;
    const difference = amount - calculatedTotal;
    const { categoryId, subcategoryId } = await getInvoiceAdjustmentCategoryTarget();

    // Remove existing manual adjustment if any
    await db
        .update(transactions)
        .set({ deletedAt: now })
        .where(
            and(
                eq(transactions.invoiceId, invoiceId),
                eq(transactions.adjustmentType, AdjustmentType.INVOICE_MANUAL_ADJUSTMENT),
                isNull(transactions.deletedAt),
            ),
        );

    // Create a visible invoice item so the manual delta becomes part of the invoice composition.
    if (difference !== 0) {
        const card = await db.select().from(creditCards).where(eq(creditCards.id, invoice[0].creditCardId));
        const cardName = card[0]?.name ?? 'Cartão';

        await db.insert(transactions).values({
            id: generateId(),
            type: TransactionType.ADJUSTMENT,
            amount: difference,
            description: difference > 0
                ? `Ajuste manual da fatura - ${cardName}`
                // "Reembolso" saiu daqui (ADR 0085): a palavra ficou reservada ao ESTORNO,
                // que abate a categoria. Este ajuste só fecha a conta da fatura e não
                // toca em relatório nenhum — chamá-lo de reembolso fazia o usuário achar
                // que tinha consertado o relatório. Linhas já gravadas mantêm o texto
                // antigo; só as novas nascem com este.
                : `Ajuste manual da fatura (a menor) - ${cardName}`,
            date: invoice[0].closingDate,
            creditCardId: invoice[0].creditCardId,
            invoiceId,
            categoryId,
            subcategoryId,
            adjustmentType: AdjustmentType.INVOICE_MANUAL_ADJUSTMENT,
            isConfirmed: 1,
            createdAt: now,
            updatedAt: now,
        });
    }

    await recalculateInvoiceTotal(invoiceId);
}

export async function clearManualTotal(invoiceId: string): Promise<void> {
    const now = new Date().toISOString();

    // Remove manual total flag
    await db
        .update(invoices)
        .set({ manualTotal: null, isManuallySet: 0, updatedAt: now })
        .where(eq(invoices.id, invoiceId));

    // Soft-delete any manual adjustment transaction
    await db
        .update(transactions)
        .set({ deletedAt: now })
        .where(
            and(
                eq(transactions.invoiceId, invoiceId),
                eq(transactions.adjustmentType, AdjustmentType.INVOICE_MANUAL_ADJUSTMENT),
                isNull(transactions.deletedAt),
            ),
        );

    await recalculateInvoiceTotal(invoiceId);
}

export async function addGenericEntry(
    invoiceId: string,
    amount: number,
    description: string,
): Promise<void> {
    const invoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (!invoice[0]) return;

    const now = new Date().toISOString();
    const { categoryId, subcategoryId } = await getInvoiceAdjustmentCategoryTarget();
    await db.insert(transactions).values({
        id: generateId(),
        type: 'expense',
        amount,
        description,
        date: invoice[0].closingDate,
        creditCardId: invoice[0].creditCardId,
        invoiceId,
        categoryId,
        subcategoryId,
        adjustmentType: AdjustmentType.INVOICE_MANUAL_ENTRY,
        isConfirmed: 1,
        createdAt: now,
        updatedAt: now,
    });

    await recalculateInvoiceTotal(invoiceId);
}

/**
 * Resolve a categoria de encargo financeiro (Impostos e Taxas → Taxas Bancárias)
 * para juros e multa por atraso. Cai no alvo de ajuste genérico (Outros → Ajustes)
 * se o usuário tiver removido/renomeado a categoria de sistema.
 */
async function getLateFeeCategoryTarget() {
    const [category] = await db
        .select({ id: categories.id })
        .from(categories)
        .where(
            and(
                eq(categories.name, 'Impostos e Taxas'),
                eq(categories.type, 'expense'),
                isNull(categories.deletedAt),
            ),
        );

    if (!category) {
        return getInvoiceAdjustmentCategoryTarget();
    }

    const [subcategory] = await db
        .select({ id: subcategories.id })
        .from(subcategories)
        .where(
            and(
                eq(subcategories.categoryId, category.id),
                eq(subcategories.name, 'Taxas Bancárias'),
                isNull(subcategories.deletedAt),
            ),
        );

    return { categoryId: category.id, subcategoryId: subcategory?.id ?? null };
}

/**
 * Lança juros e multa por atraso como uma linha rotulada de despesa na fatura,
 * categorizada como encargo bancário — entra no total a pagar e nos relatórios.
 */
export async function addLateFeeEntry(invoiceId: string, amount: number): Promise<void> {
    const invoice = await db.select().from(invoices).where(eq(invoices.id, invoiceId));
    if (!invoice[0]) return;

    const now = new Date().toISOString();
    const { categoryId, subcategoryId } = await getLateFeeCategoryTarget();
    await db.insert(transactions).values({
        id: generateId(),
        type: 'expense',
        amount,
        description: 'Juros e multa por atraso',
        date: invoice[0].closingDate,
        creditCardId: invoice[0].creditCardId,
        invoiceId,
        categoryId,
        subcategoryId,
        adjustmentType: AdjustmentType.INVOICE_MANUAL_ENTRY,
        isConfirmed: 1,
        createdAt: now,
        updatedAt: now,
    });

    await recalculateInvoiceTotal(invoiceId);
}

export async function getProjectedRecurringForInvoice(
    invoice: { creditCardId: string; openingDate: string; closingDate: string },
    materializedKeys?: Set<string>
): Promise<TransactionWithRelations[]> {
    const recurringRows = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            description: recurringTransactions.description,
            notes: recurringTransactions.notes,
            time: recurringTransactions.time,
            // Soma no total da fatura como qualquer compra ignorada; a composição analítica
            // (getAccrualExpenseTransactionsForRange) a tira por isIgnoredTransaction (ADR 0169).
            isIgnored: recurringTransactions.isIgnored,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            nextOccurrence: recurringTransactions.nextOccurrence,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            categoryId: recurringTransactions.categoryId,
            subcategoryId: recurringTransactions.subcategoryId,
            transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(recurringTransactions.subcategoryId, subcategories.id))
        .where(
            and(
                eq(recurringTransactions.creditCardId, invoice.creditCardId),
                eq(recurringTransactions.isActive, 1),
                isNull(recurringTransactions.deletedAt),
                lte(recurringTransactions.nextOccurrence, invoice.closingDate),
                sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${invoice.openingDate})`
            )
        );

    // Tags da Regra: a fatura exibe o chip e agrupa por tag, então carrega (ADR 0101).
    const tagsByRecurring = await getTagInfoForRecurringIds(recurringRows.map((row) => row.id));

    return buildProjectedRecurringTransactions(
        recurringRows.map((row) => ({
            ...row,
            tags: tagsByRecurring.get(row.id),
            creditCardName: null,
            destinationAccountName: null,
            destinationAccountColor: null,
            financingId: null,
            financingEntryType: null,
            financingInstallmentNumber: null,
        })),
        invoice.openingDate,
        invoice.closingDate,
        materializedKeys || new Set()
    ) as TransactionWithRelations[];
}

export async function getProjectedRecurringInvoiceAmount(invoice: Invoice): Promise<number> {
    const status = computeInvoiceStatus(invoice);
    if (status === 'closed' || status === 'paid') return 0;

    const materialized = await db
        .select({
            recurringId: transactions.recurringId,
            occurrenceDate: sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`,
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.invoiceId, invoice.id),
                isNull(transactions.deletedAt),
                sql`${transactions.recurringId} IS NOT NULL`
            )
        );

    const materializedKeys = new Set(materialized.map((r) => `${r.recurringId}:${r.occurrenceDate}`));

    const projected = await getProjectedRecurringForInvoice(invoice, materializedKeys);
    return projected.reduce((acc, t) => acc + (t.type === 'expense' ? t.amount : -t.amount), 0);
}

/**
 * Status da fatura de um mês, sem efeito colateral (ADR 0085).
 *
 * `getInvoiceByCardAndMonth` e `getInvoiceHistory` materializam despesa pendente e
 * projeção de recorrência no caminho de leitura; o formulário só quer saber se a
 * fatura-alvo já foi paga, e não pode criar fatura nem escrever nada só por o usuário
 * ter mexido no stepper de mês. Devolve `null` quando a fatura ainda não existe — que
 * é o normal para um mês futuro, e nunca é motivo de aviso.
 */
export async function getInvoiceStatusForMonth(
    cardId: string,
    referenceMonth: string,
): Promise<string | null> {
    if (!cardId || !referenceMonth) return null;

    const [row] = await db
        .select({
            totalAmount: invoices.totalAmount,
            paidAmount: invoices.paidAmount,
            closingDate: invoices.closingDate,
            dueDate: invoices.dueDate,
            status: invoices.status,
            dueOnBusinessDay: creditCards.dueOnBusinessDay,
        })
        .from(invoices)
        .leftJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .where(
            and(
                eq(invoices.creditCardId, cardId),
                eq(invoices.referenceMonth, referenceMonth),
            ),
        );

    return row ? computeInvoiceStatus(row) : null;
}
